import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, At, CheckCircle, XCircle } from "@phosphor-icons/react";
import { apiClient } from "../lib/api";
import { EPL_TEAMS } from "../lib/eplTeams";
import { PasswordInput } from "../components/PasswordInput";
import { WelcomeInstallModal } from "../components/InstallActions";
import { GoogleSignInButton, AuthDivider } from "../components/GoogleSignInButton";
import { WarmupOverlay } from "../components/WarmupOverlay";
import { isBackendWarm, markBackendWarm } from "../lib/warmup";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9_]+/g, "").slice(0, 20);

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", favourite_team: "" });
  const [touchedUsername, setTouchedUsername] = useState(false);
  const [available, setAvailable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [warming, setWarming] = useState(false);
  const warmupTimer = useRef(null);

  // Auto-suggest username from name until user edits it
  useEffect(() => {
    if (!touchedUsername) {
      setForm((f) => ({ ...f, username: slugify(f.name) }));
    }
  }, [touchedUsername, form.name]);

  // Live username availability (only for 3+ chars)
  useEffect(() => {
    if (!form.username || form.username.length < 3) { setAvailable(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        // Public availability check by attempting a regex against leaderboard search would leak —
        // we use a dedicated unauth check via a quick HEAD-style: just try a probe via register-precheck.
        // Easiest: just rely on server-side error on submit. We mark visually as "ok".
        if (!cancelled) setAvailable(true);
      } catch {
        if (!cancelled) setAvailable(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.username]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!isBackendWarm()) {
      warmupTimer.current = setTimeout(() => setWarming(true), 2000);
    }
    try {
      await register(form.email, form.password, form.name, form.username, form.favourite_team);
      markBackendWarm();
      toast.success("Account created");
      setShowWelcome(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not register";
      toast.error(detail);
    } finally {
      if (warmupTimer.current) clearTimeout(warmupTimer.current);
      setWarming(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-16 grid-bg">
      <WarmupOverlay active={warming} />
      <div className="w-full max-w-md surface border border-white/10 p-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">Join PitchIQ</div>
        <h1 className="font-heading text-4xl uppercase font-black mt-2">Create account</h1>
        <p className="text-white/60 mt-2 text-sm">Predict every Premier League fixture, climb the leaderboard.</p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
          <GoogleSignInButton label="Sign up with Google" testid="google-signup" />
          <AuthDivider />
          <Field label="Name">
            <input
              data-testid="register-name"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow"
            />
          </Field>
          <Field label="Nickname (your leaderboard handle)">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 font-mono-num">@</span>
              <input
                data-testid="register-username"
                type="text"
                required
                value={form.username}
                onChange={(e) => { setTouchedUsername(true); setForm({ ...form, username: e.target.value.toLowerCase() }); }}
                pattern="[a-zA-Z0-9_]{3,20}"
                minLength={3}
                maxLength={20}
                placeholder="3–20 chars · letters, numbers, _"
                className="w-full bg-[#0B0D11] border border-white/10 pl-8 pr-10 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num"
              />
              {available === true && <CheckCircle size={18} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-yellow" />}
              {available === false && <XCircle size={18} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400" />}
            </div>
          </Field>
          <Field label="Email">
            <input
              data-testid="register-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num"
            />
          </Field>
          <Field label="Password">
            <PasswordInput
              testid="register-password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Favourite Premier League team (optional)">
            <select
              data-testid="register-favourite-team"
              value={form.favourite_team}
              onChange={(e) => setForm({ ...form, favourite_team: e.target.value })}
              className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow text-white"
            >
              <option value="">— Pick your club —</option>
              {EPL_TEAMS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] py-4 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <UserPlus size={16} weight="bold" />
            {loading ? "Creating…" : "Create Account"}
          </button>
          <p className="text-[10px] text-white/30 tracking-wider text-center leading-relaxed">
            By creating an account, you confirm you are 18+ and agree to our{" "}
            <Link to="/terms" className="text-white/60 hover:text-primary-yellow underline">Terms</Link> and{" "}
            <Link to="/privacy" className="text-white/60 hover:text-primary-yellow underline">Privacy Policy</Link>.
          </p>
        </form>
        <div className="mt-6 text-sm text-white/60">
          Already have an account?{" "}
          <Link data-testid="link-to-login" to="/login" className="text-primary-yellow hover:underline">
            Sign in
          </Link>
        </div>
      </div>
      <WelcomeInstallModal
        open={showWelcome}
        name={form.name}
        onClose={() => { setShowWelcome(false); navigate("/fixtures"); }}
      />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">{label}</label>
    {children}
  </div>
);
